import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardData, getDashboard } from '../api';

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

function fmt(
  value: number | undefined | null,
  decimals = 2,
  prefix = ''
): string {
  if (value == null || isNaN(value)) return '—';
  return `${prefix}${value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

function fmtPct(value: number | undefined | null): string {
  if (value == null || isNaN(value)) return '—';
  return `${value.toFixed(1)}%`;
}

function pnlColor(v: number | undefined | null): string {
  if (v == null) return 'var(--text-primary)';
  return v >= 0 ? 'var(--accent-green)' : 'var(--accent-red)';
}

// ─────────────────────────────────────────────
// Summary Card
// ─────────────────────────────────────────────

interface SummaryCardProps {
  title: string;
  icon: string;
  children: React.ReactNode;
  linkTo?: string;
  linkLabel?: string;
}

function SummaryCard({
  title,
  icon,
  children,
  linkTo,
  linkLabel,
}: SummaryCardProps): React.ReactElement {
  return (
    <div
      className="card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '1.1rem' }}>{icon}</span>
          <span
            style={{
              fontSize: '0.72rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
            }}
          >
            {title}
          </span>
        </div>
        {linkTo && (
          <Link
            to={linkTo}
            style={{
              fontSize: '0.75rem',
              color: 'var(--accent-blue)',
              fontWeight: 500,
            }}
          >
            {linkLabel || 'View →'}
          </Link>
        )}
      </div>
      {children}
    </div>
  );
}

function DataRow({
  label,
  value,
  valueStyle,
}: {
  label: string;
  value: React.ReactNode;
  valueStyle?: React.CSSProperties;
}): React.ReactElement {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: '8px',
      }}
    >
      <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
        {label}
      </span>
      <span
        style={{
          fontSize: '0.92rem',
          fontWeight: 600,
          color: 'var(--text-primary)',
          ...valueStyle,
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────
// Market Status Strip
// ─────────────────────────────────────────────

function MarketStatusStrip({
  status,
}: {
  status: DashboardData['market_status'];
}): React.ReactElement {
  const markets: Array<{ key: keyof DashboardData['market_status']; label: string }> = [
    { key: 'US', label: '🇺🇸 US' },
    { key: 'UK', label: '🇬🇧 UK' },
    { key: 'EU', label: '🇪🇺 EU' },
    { key: 'JP', label: '🇯🇵 JP' },
    { key: 'AU', label: '🇦🇺 AU' },
  ];

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        alignItems: 'center',
      }}
    >
      <span
        style={{
          fontSize: '0.72rem',
          fontWeight: 600,
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          marginRight: '4px',
        }}
      >
        Markets
      </span>
      {markets.map(({ key, label }) => {
        const isOpen = status[key] === true;
        return (
          <span
            key={key}
            className={`market-pill ${isOpen ? 'open' : 'closed'}`}
          >
            <span className="market-dot" />
            {label} {isOpen ? 'OPEN' : 'CLOSED'}
          </span>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────
// Attention card — new watchlist setups + unread alerts side by side
// ─────────────────────────────────────────────

function AttentionCard({
  newSetups,
  unreadAlerts,
}: {
  newSetups: number;
  unreadAlerts: number;
}): React.ReactElement {
  const hasSetups  = newSetups   > 0;
  const hasAlerts  = unreadAlerts > 0;

  return (
    <SummaryCard title="Attention" icon="🔔">
      <div style={{ display: 'flex', gap: '0', flex: 1 }}>
        {/* Left — new watchlist setups */}
        <Link
          to="/watchlist"
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '12px 8px',
            gap: '4px',
            textDecoration: 'none',
            borderRadius: '8px',
            transition: 'background 0.12s',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          <span style={{
            fontSize: '2.2rem',
            fontWeight: 700,
            lineHeight: 1,
            color: hasSetups ? 'var(--accent-orange)' : 'var(--text-muted)',
          }}>
            {newSetups}
          </span>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'center' }}>
            new {newSetups === 1 ? 'setup' : 'setups'}
          </span>
        </Link>

        {/* Divider */}
        <div style={{ width: '1px', background: 'var(--border)', margin: '8px 0' }} />

        {/* Right — unread notifications */}
        <Link
          to="/notifications"
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '12px 8px',
            gap: '4px',
            textDecoration: 'none',
            borderRadius: '8px',
            transition: 'background 0.12s',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          <span style={{
            fontSize: '2.2rem',
            fontWeight: 700,
            lineHeight: 1,
            color: hasAlerts ? 'var(--accent-orange)' : 'var(--text-muted)',
          }}>
            {unreadAlerts}
          </span>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'center' }}>
            unread {unreadAlerts === 1 ? 'alert' : 'alerts'}
          </span>
        </Link>
      </div>
    </SummaryCard>
  );
}

// ─────────────────────────────────────────────
// Backfill progress bar
// ─────────────────────────────────────────────

function BackfillProgress({
  current,
  total,
}: {
  current: number;
  total: number;
}): React.ReactElement {
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;
  return (
    <div
      className="alert-strip info"
      style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '0.82rem',
        }}
      >
        <span>📡 Loading historical data: {current} of {total} instruments</span>
        <span style={{ fontWeight: 700 }}>{pct}%</span>
      </div>
      <div className="progress-bar-track">
        <div
          className="progress-bar-fill"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Main Dashboard page
// ─────────────────────────────────────────────

const MainDashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await getDashboard();
      setData(res.data);
      setError(null);
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          'Failed to load dashboard data. Retrying…'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    // Auto-refresh every 30 s
    intervalRef.current = setInterval(fetchDashboard, 30_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchDashboard]);


  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '240px',
          gap: '12px',
          color: 'var(--text-muted)',
        }}
      >
        <span className="spinner" />
        Loading dashboard…
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="alert-strip error" style={{ margin: '24px 0' }}>
        ⚠️ {error}
      </div>
    );
  }

  if (!data) return null;

  const {
    account, open_trades, history, notifications, market_status,
    new_watchlist_items, backfill_progress,
    kibar_account, kibar_open_trades, kibar_history,
  } = data;
  const currency = account?.currency || 'USD';
  const kibarCurrency = kibar_account?.currency || 'USD';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        maxWidth: '1200px',
        margin: '0 auto',
      }}
    >
      {/* ── Backfill progress (if active) ── */}
      {backfill_progress?.in_progress && (
        <BackfillProgress
          current={backfill_progress.current}
          total={backfill_progress.total}
        />
      )}

      {/* new_watchlist_items count surfaced in AttentionCard below */}

      {/* ── Market status strip ── */}
      <div className="card" style={{ padding: '14px 18px' }}>
        <MarketStatusStrip status={market_status} />
      </div>

      {/* ── 4 Summary cards ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '16px',
        }}
      >
        {/* Card 1: Portfolio */}
        <SummaryCard title="Portfolio" icon="💼" linkTo="/settings" linkLabel="Manage →">
          <DataRow
            label="Equity"
            value={`${currency} ${fmt(account?.equity)}`}
            valueStyle={{ color: 'var(--text-primary)', fontSize: '1.1rem' }}
          />
          <DataRow
            label="Available"
            value={`${currency} ${fmt(account?.available)}`}
          />
          <DataRow
            label="Margin used"
            value={`${currency} ${fmt(account?.margin)}`}
          />
        </SummaryCard>

        {/* Card 2: Open Trades */}
        <SummaryCard title="Open Trades" icon="📈" linkTo="/trades">
          <DataRow
            label="Total open"
            value={open_trades?.count ?? '—'}
            valueStyle={{ fontSize: '1.1rem' }}
          />
          <div
            style={{
              display: 'flex',
              gap: '12px',
            }}
          >
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '8px',
                backgroundColor: 'rgba(34,197,94,0.08)',
                borderRadius: '8px',
                border: '1px solid rgba(34,197,94,0.2)',
              }}
            >
              <span
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: 'var(--accent-green)',
                }}
              >
                {open_trades?.winning ?? 0}
              </span>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                Winning
              </span>
            </div>
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '8px',
                backgroundColor: 'rgba(239,68,68,0.08)',
                borderRadius: '8px',
                border: '1px solid rgba(239,68,68,0.2)',
              }}
            >
              <span
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: 'var(--accent-red)',
                }}
              >
                {open_trades?.losing ?? 0}
              </span>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                Losing
              </span>
            </div>
          </div>
          <DataRow
            label="Open P&L"
            value={`${currency} ${fmt(open_trades?.total_pnl)}`}
            valueStyle={{ color: pnlColor(open_trades?.total_pnl) }}
          />
        </SummaryCard>

        {/* Card 3: History */}
        <SummaryCard title="Performance" icon="📋" linkTo="/history">
          <DataRow
            label="Total closed"
            value={history?.total_closed ?? '—'}
            valueStyle={{ fontSize: '1.1rem' }}
          />
          <DataRow
            label="Win rate"
            value={fmtPct(history?.win_rate)}
            valueStyle={{
              color:
                history?.win_rate != null && history.win_rate >= 50
                  ? 'var(--accent-green)'
                  : 'var(--accent-red)',
            }}
          />
          <DataRow
            label="Total P&L"
            value={`${currency} ${fmt(history?.total_pnl)}`}
            valueStyle={{ color: pnlColor(history?.total_pnl) }}
          />
        </SummaryCard>

        {/* Card 4: Attention — new setups + unread alerts */}
        <AttentionCard
          newSetups={new_watchlist_items.length}
          unreadAlerts={notifications?.unread_count ?? 0}
        />
      </div>

      {/* ── Kibar section (shown only when Kibar account is configured) ── */}
      {kibar_account && (
        <>
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: '4px' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
              ⭐ Kibar Account
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
            {/* Kibar Portfolio */}
            <SummaryCard title="Kibar Portfolio" icon="💼" linkTo="/kibar" linkLabel="Watchlist →">
              <DataRow
                label="Equity"
                value={`${kibarCurrency} ${fmt(kibar_account?.equity)}`}
                valueStyle={{ color: 'var(--text-primary)', fontSize: '1.1rem' }}
              />
              <DataRow label="Available" value={`${kibarCurrency} ${fmt(kibar_account?.available)}`} />
              <DataRow label="Margin used" value={`${kibarCurrency} ${fmt(kibar_account?.margin)}`} />
            </SummaryCard>

            {/* Kibar Open Trades */}
            <SummaryCard title="Kibar Open Trades" icon="📈" linkTo="/kibar-trades">
              <DataRow label="Total open" value={kibar_open_trades?.count ?? '—'} valueStyle={{ fontSize: '1.1rem' }} />
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px', backgroundColor: 'rgba(34,197,94,0.08)', borderRadius: '8px', border: '1px solid rgba(34,197,94,0.2)' }}>
                  <span style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--accent-green)' }}>{kibar_open_trades?.winning ?? 0}</span>
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Winning</span>
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px', backgroundColor: 'rgba(239,68,68,0.08)', borderRadius: '8px', border: '1px solid rgba(239,68,68,0.2)' }}>
                  <span style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--accent-red)' }}>{kibar_open_trades?.losing ?? 0}</span>
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Losing</span>
                </div>
              </div>
              <DataRow
                label="Open P&L"
                value={`${kibarCurrency} ${fmt(kibar_open_trades?.total_pnl)}`}
                valueStyle={{ color: pnlColor(kibar_open_trades?.total_pnl) }}
              />
            </SummaryCard>

            {/* Kibar Performance */}
            <SummaryCard title="Kibar Performance" icon="📋" linkTo="/kibar-history">
              <DataRow label="Total closed" value={kibar_history?.total_closed ?? '—'} valueStyle={{ fontSize: '1.1rem' }} />
              <DataRow
                label="Win rate"
                value={kibar_history?.win_rate != null ? `${kibar_history.win_rate.toFixed(1)}%` : '—'}
                valueStyle={{
                  color: kibar_history?.win_rate != null && kibar_history.win_rate >= 50
                    ? 'var(--accent-green)' : 'var(--accent-red)',
                }}
              />
              <DataRow
                label="Total P&L"
                value={`${kibarCurrency} ${fmt(kibar_history?.total_pnl)}`}
                valueStyle={{ color: pnlColor(kibar_history?.total_pnl) }}
              />
            </SummaryCard>
          </div>
        </>
      )}

      {/* Kibar not configured placeholder */}
      {!kibar_account && (
        <div className="card" style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '1.2rem' }}>⭐</span>
          <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
            Kibar account not configured — no account named "Kibar" was found on Capital.com.
          </span>
        </div>
      )}

      {/* ── Last updated ── */}
      {error && (
        <div className="alert-strip warning" style={{ fontSize: '0.8rem' }}>
          ⚠️ {error}
        </div>
      )}
      <div
        style={{
          textAlign: 'right',
          fontSize: '0.72rem',
          color: 'var(--text-muted)',
        }}
      >
        Auto-refreshes every 30 s · Last updated {new Date().toLocaleTimeString()}
      </div>
    </div>
  );
};

export default MainDashboard;
